// src/lib/components/PlotlyCustomDatePicker.react.js
import React, {Component} from 'react';
import PropTypes from 'prop-types';
import CustomDatePicker from '../fragments/CustomDatePicker'; // Import from fragments
import dayjs from 'dayjs';

/**
 * PlotlyCustomDatePicker is a Dash wrapper for the custom date picker component.
 * It bridges the component's functionality with Dash's callback system.
 * Mimics the API of dmc.DateRangePicker where possible (using string dates).
 */
export default class PlotlyCustomDatePicker extends Component {
    render() {
        // Destructure props based on the updated Python API
        const {
            id,
            value, // Array of two strings [startDate, endDate]
            minDate,
            maxDate,
            clearable,
            amountOfMonths, // Kept for API compatibility, not used internally
            disabled,
            setProps,
            loading_state
        } = this.props;

        // Safely extract startDate and endDate from the value prop
        const startDate = (Array.isArray(value) && value.length > 0) ? value[0] : null;
        const endDate = (Array.isArray(value) && value.length > 1) ? value[1] : null;

        // Define the onChange handler that updates Dash props with the 'value' structure
        const handleDashChange = (dateStrings, dateObjects) => {
            if (setProps) {
                // Check if the new value array differs from the current prop value
                const currentValue = this.props.value || [null, null]; // Default to [null, null] if prop is initially null
                const newValue = dateStrings || [null, null]; // Ensure we have an array

                // Only call setProps if the values have actually changed
                if (newValue[0] !== currentValue[0] || newValue[1] !== currentValue[1]) {
                    // Ensure we send null instead of undefined if cleared
                    const finalValue = [
                        newValue[0] === undefined ? null : newValue[0],
                        newValue[1] === undefined ? null : newValue[1]
                    ];
                    setProps({ value: finalValue });
                }
            }
        };

        return (
            <CustomDatePicker
                id={id}
                // Pass individual dates extracted from 'value' prop
                startDate={startDate}
                endDate={endDate}
                // Rename minimumDate -> minDate
                minimumDate={minDate}
                // Pass new maxDate prop
                maxDate={maxDate}
                // Pass disabled prop
                disabled={disabled}
                // The internal component's onChange triggers this handler
                onChange={handleDashChange}
                // The clearable prop functionality is handled internally by the
                // RangePicker trigger's allowClear and the handleRangePickerChange
                // calling onChange([null, null], ...)
                // Pass loading_state if needed by CustomDatePicker fragment
                // loading_state={loading_state}
            />
        );
    }
}

// Update defaultProps to match Python and dmc conventions where applicable
PlotlyCustomDatePicker.defaultProps = {
    value: null,
    minDate: null, // Default to null, let internal component handle if needed
    maxDate: null,
    clearable: true,
    disabled: false,
    amountOfMonths: 2, // Matches dmc default, though not used by UI
};

// Update propTypes to match the new API
PlotlyCustomDatePicker.propTypes = {
    /**
     * The ID used to identify this component in Dash callbacks.
     */
    id: PropTypes.string.isRequired,

    /**
     * The selected date range [startDate, endDate] in YYYY-MM-DD format.
     */
    value: PropTypes.arrayOf(PropTypes.string),

    /**
     * The earliest selectable date in YYYY-MM-DD format.
     */
    minDate: PropTypes.string,

    /**
     * The latest selectable date in YYYY-MM-DD format.
     */
    maxDate: PropTypes.string,

    /**
     * Whether the date range can be cleared.
     */
    clearable: PropTypes.bool,

    /**
     * Number of months displayed in the calendar popover.
     * (Note: Currently fixed at 2 in the UI, prop for API compatibility).
     */
    amountOfMonths: PropTypes.number,

    /**
     * If true, the date picker is disabled and cannot be interacted with.
     */
    disabled: PropTypes.bool,

    /**
     * Dash-assigned callback that should be called to report property changes
     * to Dash, to make them available for callbacks.
     */
    setProps: PropTypes.func,

    /**
     * Object that holds the loading state object coming from dash-renderer
     */
    loading_state: PropTypes.shape({
        is_loading: PropTypes.bool,
        prop_name: PropTypes.string,
        component_name: PropTypes.string,
    }),
}; 